
import { computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useSourcesStore } from '@/stores/sources'
import { useAuthStore } from '@/stores/auth'


let hydration = null

export function loadLibrarySourceLinks(sources) {
  if (!hydration) {
    hydration = Promise.allSettled([sources.loadPlex(), sources.loadJellyfin()])
      .finally(() => { hydration = null })
  }
  return hydration
}

export function useLibrarySourceLinks() {
  const route = useRoute()
  const sources = useSourcesStore()
  const auth = useAuthStore()

  const hasPlexServers = computed(() => {
    const accounts = Array.isArray(sources.plexAccounts)
      ? sources.plexAccounts
      : []
    return accounts.some((acc) => {
      const servers = Array.isArray(acc?.servers) ? acc.servers : []
      return servers.length > 0
    })
  })

  onMounted(() => loadLibrarySourceLinks(sources))

  const tabs = computed(() => {
    const out = []

    // "All Libraries" -- cross-source aggregator view (Put.io + every
    // reachable Plex server). Lives at the head of the strip so it's
    // the first thing users see across every /library/* and
    // /sources/plex/* page. Spec:
    // docs/research/unified-library-search.md (## All Libraries view
    // spec).
    if (hasPlexServers.value || route.name === 'library-all') {
      out.push({
        key: 'all',
        label: 'All Libraries',
        to: { name: 'library-all' },
        active: route.name === 'library-all',
        iconColor: 'bg-purple-500',
      })
    }

    // Put.io is always present (it's the primary source). The `library`
    // route's canonical path is `/sources/putio`; `/library` and
    // `/library/putio` are aliases. Match either by route name (covers
    // all three URLs in one check) and additionally accept the legacy
    // path strings for defensive belt-and-braces — previously this
    // checked only the alias paths, so a click on the tab navigated to
    // the canonical `/sources/putio` and then NO tab matched, which
    // triggered the "All Libraries fallback active" branch below and
    // looked to the user like Put.io selected All Libraries.
    out.push({
      key: 'putio',
      label: 'Put.io',
      to: { name: 'library' },
      active:
        route.name === 'library' ||
        route.path === '/library' ||
        route.path === '/library/putio' ||
        route.path === '/sources/putio',
      iconColor: 'bg-blue-500',
    })

    // CLAUDE-TODO #358: YouTube sits alongside Put.io + Plex as a browsable
    // library. Gated on the same permission as the home "Browse YouTube" chip
    // (LibraryShortcuts.vue) so library-only invites that can't see YouTube
    // tiles don't get the tab either.
    if (auth.canBrowseTorrents) {
      out.push({
        key: 'youtube',
        label: 'YouTube',
        to: { name: 'youtube-home' },
        active: route.name === 'youtube-home',
        iconColor: 'bg-red-500',
      })
    }

    // Plex: one tab per (account, server). Hidden when the calling
    // invite has no Plex accounts.
    const accounts = Array.isArray(sources.plexAccounts)
      ? sources.plexAccounts
      : []
    for (const acc of accounts) {
      const servers = Array.isArray(acc?.servers) ? acc.servers : []
      for (const server of servers) {
        const serverId = String(server.id)
        // Read accountId from the SERVER not the account. For Feature-28
        // sharees the synthetic "Shared by ..." account carries
        // accountId: null (sources-plex.js:401) so accountId-on-account
        // skipped every shared server. shapeServer now exposes the
        // owning account_id per-server (the same id `/recently-added`
        // already uses on line 975 to construct browse links), so the
        // tab renders identically for owned + shared.
        const accountId = String(server.accountId ?? acc.accountId ?? acc.id ?? '')
        if (!accountId || !serverId) continue
        const isActive =
          String(route.params.serverId || '') === serverId &&
          String(route.params.accountId || '') === accountId
        out.push({
          key: `plex-${accountId}-${serverId}`,
          label: server.name || 'Plex',
          to: {
            name: 'sources-plex-browse',
            params: { accountId, serverId },
          },
          active: isActive,
          iconColor: 'bg-amber-500',
        })
      }
    }

    // Synthetic fallback: if the user is currently ON a Plex per-server
    // page (sources-plex-browse) but no tab above matches their route
    // params, inject one derived from the URL. Covers two cases that
    // were leaving the strip without a Plex pill at all:
    //   1. First-paint race — sources.loadPlex() hasn't resolved yet.
    //   2. plexAccounts came back empty even though the user reached
    //      this gated route (transient API blip, sharee account that
    //      Feature 28 didn't include in /api/sources/plex/accounts).
    // Either way the user sees the page they're on represented in the
    // tab strip. Once loadPlex resolves the real tab replaces this one
    // on the next tick (same key shape, so reconciliation is clean).
    const routeAccountId = String(route.params.accountId || '')
    const routeServerId = String(route.params.serverId || '')
    if (
      route.name === 'sources-plex-browse' &&
      routeAccountId &&
      routeServerId &&
      !out.some((t) => t.key === `plex-${routeAccountId}-${routeServerId}`)
    ) {
      out.push({
        key: `plex-${routeAccountId}-${routeServerId}`,
        label: 'Plex',
        to: {
          name: 'sources-plex-browse',
          params: { accountId: routeAccountId, serverId: routeServerId },
        },
        active: true,
        iconColor: 'bg-amber-500',
      })
    }

    // Jellyfin: one tab per server. Routes to /library/jellyfin/:id when
    // a Jellyfin browse view exists -- currently /sources/jellyfin/:id
    // hasn't been built (the per-server browse experience is still on
    // the player route), so we skip these entries for now. Left as a
    // structural slot so a future PR can wire them up by adding the
    // route.
    // (Intentional gap.)

    // Fallback active highlight: every render must mark exactly one tab
    // as active so the strip never looks orphaned. If nothing matched
    // (e.g. user on a /sources/jellyfin/* route we don't surface yet),
    // promote the leftmost "All Libraries" entry — it's the always-
    // available cross-source surface, so defaulting to it never lands
    // the highlight on something the user can't navigate to.
    if (!out.some((t) => t.active) && out.length > 0) {
      out[0].active = true
    }

    return out
  })
  return { tabs }
}
